import { Injectable } from "@angular/core";
import { TripService } from "app/modules/components/reports/visits/visit-report/visit-report.service";
import { ExtendsTrips, FilterVisitPayload, FrecuencyData, GroupData, MapFilters } from "./models/map-modal.interface";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MapModalOrderService } from "./services/map-modal-order.service";
import { MapModalRouteService } from "./services/map-modal-route.service";
import { IResult } from "app/common/interfaces";
import { BusinessPartnerBySubRoute } from "app/modules/components/routing/sub-routes/interfaces/busines-partner-by-sub-route.interface";
import { SubRouteFrequenciesBPService } from "app/modules/components/routing/sub-routes/sub-routes-frequency/sub-route-frequencies-bp.service";
import { MapModalTripService } from "./services/map-modal.trip.service";

@Injectable({
  providedIn: "root",
})
export class MapModalViewModel {

  daysList: { code: number; day: string }[] = [
    {code: 1, day: "LU"},
    {code: 2, day: "MA"},
    {code: 3, day: "MI"},
    {code: 4, day: "JU"},
    {code: 5, day: "VI"},
    {code: 6, day: "SA"},
    {code: 7, day: "DO"}
  ];

  constructor(
    private readonly orderService: MapModalOrderService,
    private readonly visitService: TripService,
    private readonly routeService: MapModalRouteService,
    private readonly subRouteFrequenciesBPService: SubRouteFrequenciesBPService,  
    private readonly mapModalTripService: MapModalTripService,
    private snackBar: MatSnackBar,
  ) {
  }

  //#region Agrupados
  async getGroupOrderShipTo(
    pageSizeCustom: number,
    pageIndexCustom: number,
    sortActiveCustom,
    tripId: number,
    document_type: string[],
    filter: {
      groupIds: string[],
      shipTo: string[],
      statusIds?: number[],
      isDirectSale?: boolean
    },
  ): Promise < GroupData[] > {
    const sortDirection = "ASC";

    try {
      const response = await this.orderService.findAllOrderGroupShipTo(
          {
            currentPage: pageIndexCustom,
            limitPerPage: pageSizeCustom,
            search: '',
            sort: sortActiveCustom,
            sortDirection: sortDirection,
          },
          tripId,
          document_type,
          filter
        )
        .toPromise();
      return response?.data;
    } catch(error) {
      this.snackBar.open(error.error.message || 'Error al obtener grupos de socios de negocio', 'Cerrar', { duration: 3000 });
      throw error;
    }
  }

  async getGroupOrderShipToWithExtraFilters(
    pageSizeCustom: number,
    pageIndexCustom: number,
    sortActiveCustom,
    tripId: number,
    document_type: string[],
    filter: MapFilters
  ): Promise<GroupData[]> {
    const sortDirection = "ASC";

    try {
      const response = await this.orderService.findAllOrderGroupShipToWithExtraFilters(
        {
          currentPage: pageIndexCustom,
          limitPerPage: pageSizeCustom,
          search: '',
          sort: sortActiveCustom,
          sortDirection: sortDirection,
        },
        tripId,
        document_type,
        filter
      ).toPromise();

      return response?.data;
    } catch (error) {
      this.snackBar.open(error.error?.message || 'Error al obtener pedidos', 'Cerrar', { duration: 3000 });
      throw error;
    }
  }
  //#endregion

  //#region Viaje
  async getGroupTripShipTo(
    pageSizeCustom: number,
    pageIndexCustom: number,
    sortActiveCustom,
    tripId: number,
    filter: {
      groupIds: string[],
      shipTo: string[]
    }
  ): Promise< GroupData[]> {
    const sortDirection = "ASC";

    try {
      const response = await this.visitService.findAllTripGroupShipTo(
          {
            currentPage: pageIndexCustom,
            limitPerPage: pageSizeCustom,
            search: '',
            sort: sortActiveCustom,
            sortDirection: sortDirection,
          },
          tripId,
          filter
        )
        .toPromise();
      return response?.data;
    } catch (error) {
      this.snackBar.open(error.error.message || 'Error al obtener visitas de los socios de negocio', 'Cerrar', { duration: 3000 });
      throw error;
    }
  }
  //#endregion

  //#region Frecuency
  async getGroupRoute(
    pageSizeCustom: number,
    pageIndexCustom: number,
    sortActiveCustom,
    subRouteId: string,
    type: boolean,
    filter: {
      groupIds: string[],
      shipTo: string[]
    }
  ): Promise< BusinessPartnerBySubRoute[]> {
    const sortDirection = "ASC";

    try {
      const response = await this.routeService.findAllWithCountBySubRoute(
          {
            currentPage: pageIndexCustom,
            limitPerPage: pageSizeCustom,
            search: '',
            sort: sortActiveCustom,
            sortDirection: sortDirection,
          },
          subRouteId,
          type,
        )
        .toPromise();
      return response?.data?.data;
    } catch (error) {
      this.snackBar.open(error.error.message || 'Error al obtener frecuencias', 'Cerrar', { duration: 3000 });
      throw error;
    }
  }
  //#endregion

  //#region Frecuency Route
  getFrecuencyDays(days: number[]): string {
    return days
      .map((dayCode) => this.daysList.find((day) => day.code === dayCode)?.day)
      .join('-');
  }

  private buildFrecuencyDataWithGroup<T>(
    data: T[],
    group
  ): FrecuencyData<T>[] {
    const groupId = String(group._id);
    const map = new Map<string, FrecuencyData<T>>();

    if (!map.has(groupId)) {
      map.set(groupId, { group, shipTo: [] });
    }

    for (const item of data) {
      map.get(groupId)!.shipTo.push(item);
    }

    return Array.from(map.values());
  }

  fetchBusinessbyFrequency(
    subRouteId: string,
    frecuencyRoute,
    onSuccess: (data: FrecuencyData[]) => void
  ): void {
    this.subRouteFrequenciesBPService.findClientByFrequency(subRouteId, frecuencyRoute._id).subscribe({
      next: (res: IResult<BusinessPartnerBySubRoute>) => {
        const groupName = this.getFrecuencyDays(frecuencyRoute?.days || []);
        const customGroup = {
          _id: frecuencyRoute._id,
          name: groupName,
        };

        const mapped = this.buildFrecuencyDataWithGroup(res.data || [], customGroup);
        onSuccess(mapped);        
      },
      error: (err) => {
        this.snackBar.open(err.error.message || "Error al obtener frecuencias.","Cerrar",{ duration: 5000});
      },
    });
  }

  getTodayDayCode(): number {
    const today = new Date().getDay();
    return today === 0 ? 7 : today;    // convertir 0 (Domingo) en 7
  }

  async fetchBusinessbyWeekDay(
    subRouteId: string,
    frecuencyRoute
  ): Promise<FrecuencyData[]> {
    const todayDay = this.getTodayDayCode();
    const dayParam = frecuencyRoute ? frecuencyRoute : todayDay;

    try {
      const res = await this.subRouteFrequenciesBPService
        .findClientByWeekDay(subRouteId, dayParam)
        .toPromise();

      const todayInfo = this.daysList.find(d => d.code === dayParam);
      const customGroup = {
        _id: dayParam,
        name: todayInfo ? todayInfo.day : todayDay.toString(),
      };

      return this.buildFrecuencyDataWithGroup(res?.data || [], customGroup);
    } catch (err) {
      this.snackBar.open(err.error?.message || "Error al traer frecuencias de socio de negocio.","Cerrar",{ duration: 5000 });
      throw err;
    }
  }
  //#endregion

  //#region Trip Global Route
  async findAllTripGlobal(filter:FilterVisitPayload, isLastVisit: boolean = false): Promise<{
    trips: ExtendsTrips[];
    visitNames: string[];
  }> {
    try {
      const res = await this.mapModalTripService
        .findAllRouteTripFilter(filter)
        .toPromise();

      let trips: ExtendsTrips[] = Array.isArray(res?.data) ? res.data : [];

      // Sacar los nombres únicos de las visitas
      const namesSet = new Set<string>();
      trips = trips.map(trip => {
        if (isLastVisit) {
          const lastVisit = trip.visits?.[trip.visits.length - 1];
          if (lastVisit?.name) {
            namesSet.add(lastVisit.name);
          }
          return {
            ...trip,
            visits: lastVisit ? [lastVisit] : []
          };
        } else {
          (trip.visits || []).forEach(visit => {
            if (visit?.name) {
              namesSet.add(visit.name);
            }
          });
          return trip;
        }
      });
      const visitNames = Array.from(namesSet);

      return { trips, visitNames };
    } catch (err) {
      this.snackBar.open(err.error?.message || "Error al obtener viajes.","Cerrar",{ duration: 5000 });
      return { trips: [], visitNames: [] };
    }
  }
  //#endregion
}